import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getApplications } from '../../../services/application';
import { LoadingScreen } from '../../../components/loadingScreen';
import {
  Main,
  ApplicationContainer,
  ApplicationCardWrapper,
  CardHeader,
  TagsContainer,
  ProjectTypeTag,
  ApplicationId,
  AddressRow,
  LocationIcon,
  CardFooter,
  SubmittedDate,
  ViewDetailsButton,
  ChevronIcon,
  WelcomeContainer,
  WelcomeHeading,
  WelcomeSubtext,
  ApplicationTypeTag,
} from './application.style';
import { EmptyStateScreen } from '../../../components/state';
import { Pagination } from '../../../components/uielements';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { IntlMessages } from '../../../helper/langHelper';

const WelcomeHeader = ({ username }) => {
  // Get time of day for a more accessible greeting
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return <IntlMessages id="text.morning" />;
    if (hour < 18) return <IntlMessages id="text.afternoon" />;
    return <IntlMessages id="text.evening" />;
  };

  return (
    <WelcomeContainer role="region" aria-labelledby="welcome-heading">
      <WelcomeHeading id="welcome-heading" tabIndex="0">
        <IntlMessages id="text.good" /> {getTimeOfDay()} {username}
      </WelcomeHeading>
      <WelcomeSubtext aria-live="polite">
        <IntlMessages id="welcome.message" />
      </WelcomeSubtext>
    </WelcomeContainer>
  );
};

const ApplicationCard = ({ application, isFirst }) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/application/${application._id}`);
  };

  return (
    <ApplicationCardWrapper
      className="application_card"
      data-tour={isFirst ? 'first-card' : undefined}
    >
      <CardHeader>
        <TagsContainer>
          <ProjectTypeTag>{application?.procedureId?.procedureName}</ProjectTypeTag>
          {application?.type && (
            <ApplicationTypeTag
              color={application?.type === 'Pre-lodgement' ? '#A67C00' : '#2E7D32'}
              backgroundColor={application?.type === 'Pre-lodgement' ? '#f9eec3' : '#def9e0'}
            >
              {application?.type}
            </ApplicationTypeTag>
          )}
        </TagsContainer>
      </CardHeader>

      <AddressRow>
        <LocationIcon size={20} />
        {application?.address}
      </AddressRow>

      <CardFooter>
        <SubmittedDate>
          <IntlMessages id="card.text.submitted.date" />:{' '}
          {new Date(application?.createdAt).toLocaleDateString('en-GB')}
        </SubmittedDate>
        <ViewDetailsButton
          onClick={handleViewDetails}
          aria-label={`View details for ${application?.procedureId?.procedureName}`}
          className="application_card_details"
          data-tour={isFirst ? 'first-card-details' : undefined}
        >
          <IntlMessages id="card.text.view.details" />
          <ChevronIcon size={16} />
        </ViewDetailsButton>
      </CardFooter>
    </ApplicationCardWrapper>
  );
};

const Application = () => {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();

  const { userInfo, isLoading: isUserInfoLoading } = useUserInfo();

  // React Query for applications list
  const { data: applications, isLoading: isApplicationListLoading } = useQuery({
    queryKey: ['applications', page],
    queryFn: () => getApplications({ pageNumber: page, perPage: 10 }),
    // staleTime: 5 * 60 * 1000, // 5 minutes
    // cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleAddressSearch = () => {
    navigate('/addressSearch', { replace: true });
  };

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  if (isApplicationListLoading || isUserInfoLoading) {
    return <LoadingScreen loadingText={<IntlMessages id="application.loding.message" />} />;
  }

  return (
    <Main role="main">
      <ApplicationContainer>
        <WelcomeHeader username={userInfo?.username} />
        {applications?.applicationList?.length > 0 ? (
          <>
            {applications?.applicationList?.length > 0 &&
              applications?.applicationList?.map((applicationInfo, index) => (
                <ApplicationCard
                  key={applicationInfo._id || index}
                  application={applicationInfo}
                  isFirst={index === 0}
                />
              ))}
            <Pagination
              page={page}
              pageSize={10}
              prevPage={handlePreviousPage}
              nextPage={handleNextPage}
              total={applications?.totalCount}
            />
          </>
        ) : (
          <EmptyStateScreen buttonText={'Create Application'} onClick={handleAddressSearch} />
        )}
      </ApplicationContainer>
    </Main>
  );
};

export default Application;
